import { useState } from "react";
import { ChevronDownIcon, Loader2Icon, WalletIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/overlay";
import { Input } from "../ui/form";
import { cn } from "../lib/cn";
import { short, sum } from "../lib/format";
import type { PrivateKeyAccount } from "viem/accounts";
import { isSettingsPointer } from "@woodpecker/protocol";
import { useT, Y, type TextRef } from "../i18n";
import { useStore } from "../store/store";
import { useWallet, type SignLine } from "../store/wallet";
import { HttpError, ServiceClient, signInTo } from "../adapters/clients";
import { consoleIdentityOf, deriveRoot, rootOf } from "../adapters/identity";
import { isPrivateKey, sessionOf } from "../adapters/wallet";
import { Rejected } from "./common";
import { GAS, authorizeText, useSignLines } from "./signing";

/** The host of a service's origin, for the challenge line of the signing panel. */
function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

/**
 * The identity root line (D73): one deterministic signature of the wallet, once per browser; the Console's own
 * identity and the extensions' identities derive from it. A message signature, no gas; the root stays in this browser.
 */
function rootLine(account: PrivateKeyAccount, chainId: number, onRoot: (root: `0x${string}`) => void): SignLine {
  return {
    plain: Y("wallet.plain.identityRoot"),
    text: Y("wallet.line.identityRoot", { chainId }),
    run: async () => {
      onRoot(await deriveRoot(chainId, account));
    },
  };
}

/**
 * The challenge line of one service (D47): a message signature, no gas; `after` runs once the session exists and
 * may add lines after this one (the sign-in that learns which memory holds the settings record).
 */
function challengeLine(
  service: string,
  origin: string,
  plain: TextRef,
  account: PrivateKeyAccount,
  chainId: number,
  after?: () => Promise<SignLine[]>,
): SignLine {
  return {
    plain,
    text: Y("wallet.line.challenge", { service, domain: hostOf(origin), nonce: "…", minutes: 5 }),
    run: async () => {
      await signInTo(origin, account, chainId, { service });
      return after ? { more: await after() } : undefined;
    },
  };
}

/** The total of the wallet in wp at today's rates. */
export function useTotalWp(): number {
  const { balances, wpPerToken } = useStore();
  return sum(...Object.entries(balances).map(([sym, n]) => n * wpPerToken(sym)));
}

/**
 * The sign-in request (D47): the runner's and the head memory's challenges, two message signatures, no gas; each line
 * names its own service's origin (issue #7 item 3). The head memory keeps only a pointer when the wallet chose another
 * memory (issue #2): once the head's session exists its record is read, and a pointer adds the chosen memory's
 * challenge to the same request, so one flow completes a restore from the key alone (issue #7 item 2). A service
 * whose session is still valid asks for no signature.
 */
/**
 * The identity sign-in to any service (D47a, D73): `silent` signs in with the Console's identity where the service
 * knows it and contract A shows a cap, with no wallet signature; `register` names the identity to the services the
 * wallet holds its own session with; `ensure` gets a session with a service one way or the other (the wallet's
 * challenge in the panel when the identity cannot), as a store needs before an extension's first put.
 */
export function useIdentitySignIn() {
  const { wallet, config, chain, entry } = useStore();
  const ensureSessions = useEnsureSessions();
  const silent = async (origin: string, service: string): Promise<boolean> => {
    if (!wallet) return false;
    const address = wallet.address;
    const r = rootOf(config.chainId, address);
    if (!r) return false;
    const id = consoleIdentityOf(r);
    try {
      const known = await new ServiceClient(origin, address, config.chainId).consoleIdentity();
      if (!known || known.toLowerCase() !== id.address.toLowerCase()) return false;
      let authorized = false;
      for (const token of await chain.tokensOf(address, id.address)) {
        const a = await chain.authorizationOf(address, id.address, token);
        if (a.cap > a.used) authorized = true;
      }
      if (!authorized) return false;
      await signInTo(origin, id, config.chainId, { service, wallet: address });
      return true;
    } catch {
      return false;
    }
  };
  const register = async (origins: string[]) => {
    if (!wallet) return;
    const address = wallet.address;
    const r = rootOf(config.chainId, address);
    if (!r) return;
    const id = consoleIdentityOf(r).address;
    for (const origin of origins) {
      const s = sessionOf(origin, address);
      if (!s || s.identity) continue;
      const client = new ServiceClient(origin, address, config.chainId);
      try {
        if ((await client.consoleIdentity())?.toLowerCase() !== id.toLowerCase()) await client.registerConsole(id);
      } catch {
        /* a service older than the registration, or unreachable: the wallet's session serves */
      }
    }
  };
  const ensure = async (name: string): Promise<boolean> => {
    const e = entry(name);
    if (!wallet || !e) return false;
    if (sessionOf(e.origin, wallet.address)) return true;
    if (await silent(e.origin, e.name)) return true;
    const ok = await new Promise<boolean>((resolve) =>
      ensureSessions([name], resolve, Y("wallet.req.signInService", { name })),
    );
    if (ok) await register([e.origin]);
    return ok;
  };
  return { silent, register, ensure };
}

export function useSignIn() {
  const {
    wallet,
    runnerOrigin,
    headMemory,
    entry,
    clientFor,
    settings,
    config,
    chain,
    setSignedIn,
    identityRoot,
    setIdentityRoot,
  } = useStore();
  const { request } = useWallet();
  const t = useT();
  return (onDone: (ok: boolean) => void) => {
    const head = entry(headMemory);
    if (!wallet || !runnerOrigin || !head) return;
    const { account, address } = wallet;
    // the identity root (D73) comes first when this browser holds none yet
    const root: SignLine | null = identityRoot ? null : rootLine(account, config.chainId, setIdentityRoot);
    /**
     * The Console's identity signs in for the wallet where it can (D47a, D73): the service knows it as the wallet's
     * Console identity, and contract A shows a cap for it; a message signed in the browser, no panel line. Anything
     * else falls back to the wallet's own challenge in the panel.
     */
    const silent = async (origin: string, service: string): Promise<boolean> => {
      const r = identityRoot ?? rootOf(config.chainId, address);
      if (!r) return false;
      const id = consoleIdentityOf(r);
      try {
        const known = await new ServiceClient(origin, address, config.chainId).consoleIdentity();
        if (!known || known.toLowerCase() !== id.address.toLowerCase()) return false;
        let authorized = false;
        for (const token of await chain.tokensOf(address, id.address)) {
          const a = await chain.authorizationOf(address, id.address, token);
          if (a.cap > a.used) authorized = true;
        }
        if (!authorized) return false;
        await signInTo(origin, id, config.chainId, { service, wallet: address });
        return true;
      } catch {
        return false;
      }
    };
    /** The wallet names its Console identity to each service it holds its own session with (D73); best effort. */
    const register = async (origins: string[]) => {
      const r = identityRoot ?? rootOf(config.chainId, address);
      if (!r) return;
      const id = consoleIdentityOf(r).address;
      for (const origin of origins) {
        const s = sessionOf(origin, address);
        if (!s || s.identity) continue;
        const client = new ServiceClient(origin, address, config.chainId);
        try {
          if ((await client.consoleIdentity())?.toLowerCase() !== id.toLowerCase()) await client.registerConsole(id);
        } catch {
          /* a service older than the registration, or unreachable: the wallet's session serves */
        }
      }
    };
    const origins = [runnerOrigin, head.origin];
    // the chosen memory's challenge: the head's record points at it and the browser holds no session with it
    const chosenLines = async (): Promise<SignLine[]> => {
      const client = clientFor(headMemory);
      if (!client) return [];
      const record = await client.settings();
      if (!isSettingsPointer(record, headMemory)) return [];
      const chosen = entry(record.defaultMemory);
      if (!chosen) return [];
      origins.push(chosen.origin);
      if (sessionOf(chosen.origin, address) || (await silent(chosen.origin, chosen.name))) return [];
      const plain = Y("wallet.plain.signInMemoryNamed", { name: chosen.name });
      return [challengeLine(chosen.name, chosen.origin, plain, account, config.chainId)];
    };
    void (async () => {
      const lines: SignLine[] = [];
      if (root) lines.push(root);
      if (!sessionOf(runnerOrigin, address) && !(await silent(runnerOrigin, settings.defaultRunner)))
        lines.push(
          challengeLine(settings.defaultRunner, runnerOrigin, Y("wallet.plain.signIn"), account, config.chainId),
        );
      const headLine = challengeLine(
        headMemory,
        head.origin,
        Y("wallet.plain.signInMemory"),
        account,
        config.chainId,
        chosenLines,
      );
      if (!sessionOf(head.origin, address) && !(await silent(head.origin, headMemory))) lines.push(headLine);
      else {
        // a session the head no longer accepts (401): sign in again and read after it; any other failure of the read
        // (the head unreachable) adds no line, and the settings load reports it once signed in
        lines.push(
          ...(await chosenLines().catch((e: unknown) =>
            e instanceof HttpError && e.status === 401 ? [headLine] : [],
          )),
        );
      }
      if (lines.length === 0) {
        await register(origins);
        setSignedIn(true);
        onDone(true);
        return;
      }
      request({
        title: Y("wallet.req.signIn"),
        note: Y("wallet.req.noFee"),
        lines,
        onDone: (r, states) => {
          if (r === "signed") {
            void register(origins).finally(() => {
              setSignedIn(true);
              onDone(true);
            });
          } else {
            if (r === "failed")
              toast.error(t("wallet.signInFailed", { detail: states.find((l) => l.error)?.error ?? "" }));
            onDone(false);
          }
        },
      });
    })();
  };
}

/**
 * The cap the Console's identity needs to pay a write of the settings record to a memory service (D75): asked in the
 * signing panel on first use (approve the memory's token when contract A may not move it yet, then authorize), and
 * raised by the memory's published default when it is used up. Resolves true when every memory named can be written.
 */
export function useEnsurePayerCap() {
  const { wallet, identityRoot, setIdentityRoot, chain, entry, approvedTokens, config } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  return async (memoryNames: string[]): Promise<boolean> => {
    if (!wallet) return false;
    // the identity: from the root this browser holds, or derived by the first line of the request below
    const root = identityRoot ?? rootOf(config.chainId, wallet.address);
    const known = root ? consoleIdentityOf(root) : null;
    const needed: SignLine[] = [];
    if (!known) needed.push(rootLine(wallet.account, config.chainId, setIdentityRoot));
    const approving = new Set<string>();
    for (const name of [...new Set(memoryNames)]) {
      const e = entry(name);
      if (!e) continue;
      const price = Math.max(1, Math.ceil(e.price.max));
      const a = known
        ? await chain.authorizationOf(wallet.address, known.address, e.tokenAddress)
        : { cap: 0n, used: 0n };
      if (Number(a.cap - a.used) >= price) continue;
      if (!approvedTokens.has(e.token) && !approving.has(e.token)) {
        approving.add(e.token);
        needed.push(lines.approve(e.token));
      }
      const cap = Number(a.used) + Math.max(e.defaultCap ?? 20, price);
      const plain = Y("wallet.plain.payerCap", { cap, token: e.token, memory: name, price });
      if (known) {
        needed.push(lines.authorize(plain, known.address, { [e.token]: cap }));
      } else {
        // the identity is derived by the root line above, so this line reads it when it runs
        needed.push({
          plain,
          text: authorizeText(t("settings.record.identity"), e.token, cap),
          gas: GAS.authorize,
          run: async () => {
            const r = rootOf(config.chainId, wallet.address);
            if (!r) throw new Error(t("wallet.payer.noRoot"));
            const id = consoleIdentityOf(r).address;
            return { tx: (await chain.authorize(wallet.address, id, e.tokenAddress, BigInt(cap))).tx };
          },
        });
      }
    }
    if (needed.length === 0) return true;
    return new Promise<boolean>((resolve) => {
      request({
        title: Y("wallet.req.payerCap"),
        note: Y("wallet.req.payerCapNote", { identity: known ? short(known.address) : "…" }),
        lines: needed,
        onDone: (r, states) => {
          if (r === "failed")
            toast.error(t("wallet.signInFailed", { detail: states.find((l) => l.error)?.error ?? "" }));
          resolve(r === "signed");
        },
      });
    });
  };
}

/**
 * The sign-ins a switch of the default memory needs (issue #2): the new memory and the head, for the ones without a
 * session yet, as message signatures in the panel; `onDone(true)` when every session exists.
 */
export function useEnsureSessions() {
  const { wallet, entry, config, sessionWith } = useStore();
  const { request } = useWallet();
  const t = useT();
  return (names: string[], onDone: (ok: boolean) => void, title?: TextRef) => {
    if (!wallet) return onDone(false);
    const missing = [...new Set(names)].filter((n) => !sessionWith(n)).flatMap((n) => entry(n) ?? []);
    if (missing.length === 0) return onDone(true);
    request({
      title: title ?? Y("wallet.req.signInMemories"),
      note: Y("wallet.req.noFee"),
      lines: missing.map((e) =>
        challengeLine(
          e.name,
          e.origin,
          Y("wallet.plain.signInMemoryNamed", { name: e.name }),
          wallet.account,
          config.chainId,
        ),
      ),
      onDone: (r, lines) => {
        if (r === "failed") toast.error(t("wallet.signInFailed", { detail: lines.find((l) => l.error)?.error ?? "" }));
        onDone(r === "signed");
      },
    });
  };
}

export function WalletBlock({ onPick, className }: { onPick?: () => void; className?: string }) {
  const { wallet, signedIn, config, newWallet, setWalletKey, runnerOrigin, headMemory, entry, directoryLoading } =
    useStore();
  const { queue, popoverOpen, panelOpen, openPopover, closePopover } = useWallet();
  const t = useT();
  const total = useTotalWp();
  const signIn = useSignIn();
  const [rejected, setRejected] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [keyText, setKeyText] = useState("");
  const busy = queue.length > 0;
  const open = popoverOpen || panelOpen;
  const frame = cn(
    "flex w-full flex-col gap-1 rounded-xl border bg-background px-3 py-2.5 text-left shadow-xs transition-all duration-250",
    (busy || open) && "-translate-y-1 shadow-lg",
    busy ? "ring-2 ring-amber-500/60" : open && "ring-2 ring-primary/20",
    className,
  );
  const dot = busy && (
    <span className="absolute -top-1 -right-1 size-2 animate-pulse rounded-full bg-amber-500 ring-2 ring-background" />
  );

  if (!wallet) {
    return (
      <div className={frame}>
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <WalletIcon className="size-4 text-foreground" />
          {t("wallet.none.title")}
        </span>
        <p className="text-xs text-muted-foreground">{t("wallet.none.hint")}</p>
        <Button
          size="sm"
          className="mt-1.5 w-full"
          onClick={() => {
            const w = newWallet();
            toast.success(t("wallet.created", { address: short(w.address) }));
          }}
        >
          <WalletIcon />
          {t("wallet.create")}
        </Button>
        <Button size="sm" variant="outline" className="w-full" onClick={() => setImportOpen(true)}>
          {t("wallet.import.title")}
        </Button>
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("wallet.import.title")}</DialogTitle>
              <DialogDescription>{t("wallet.import.description")}</DialogDescription>
            </DialogHeader>
            <Input
              className="font-mono"
              placeholder={t("wallet.import.placeholder")}
              value={keyText}
              onChange={(e) => setKeyText(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            {keyText.trim() !== "" && !isPrivateKey(keyText) && (
              <p className="text-xs text-destructive">{t("wallet.import.invalid")}</p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                disabled={!isPrivateKey(keyText)}
                onClick={() => {
                  if (!isPrivateKey(keyText)) return;
                  setWalletKey(keyText.trim() as `0x${string}`);
                  setImportOpen(false);
                  setKeyText("");
                  toast.success(t("wallet.imported", { address: "" }));
                }}
              >
                {t("wallet.import.title")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (!signedIn) {
    const canSignIn = !!runnerOrigin && !!entry(headMemory) && !directoryLoading;
    return (
      <div className={frame}>
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="relative">
            <WalletIcon className="size-4 text-foreground" />
            {dot}
          </span>
          <span className="font-mono text-foreground">{short(wallet.address)}</span>
        </span>
        <span className="text-xs text-muted-foreground">{t("signedOut.title")}</span>
        <Button
          size="sm"
          className="mt-1.5 w-full"
          disabled={busy || !canSignIn}
          onClick={() => {
            onPick?.();
            setRejected(false);
            signIn((ok) => setRejected(!ok));
          }}
        >
          {busy ? <Loader2Icon className="animate-spin" /> : <WalletIcon />}
          {t("wallet.block.signIn")}
        </Button>
        {!canSignIn && !directoryLoading && <p className="text-xs text-destructive">{t("deploy.prereq.noRunner")}</p>}
        {rejected && !busy && <Rejected className="text-xs" />}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label={t("wallet.block.open")}
      aria-expanded={popoverOpen}
      onClick={() => {
        onPick?.();
        if (popoverOpen) closePopover();
        else openPopover();
      }}
      className={cn(frame, "hover:bg-muted/60")}
    >
      <span className="flex items-center gap-2">
        <span className="relative">
          <WalletIcon className="size-4" />
          {dot}
        </span>
        <span className="font-mono text-sm">{short(wallet.address)}</span>
        <ChevronDownIcon
          className={cn("ml-auto size-4 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </span>
      <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          {config.networkName}
        </span>
        <span className="font-medium text-foreground tabular-nums">{t("common.wp", { n: total })}</span>
      </span>
    </button>
  );
}
